import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface ContactFormProps {
  className?: string;
  service?: string;
  source?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  className = '',
  service = 'general',
  source = 'contact-section'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    service: service,
    source: source
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Il nome è obbligatorio');
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Email non valida');
      return false;
    }
    if (!formData.message.trim()) {
      toast.error('Il messaggio è obbligatorio');
      return false;
    }
    return true;
  };

  const trackConversion = (leadData: any) => {
    // Google Analytics 4 Event Tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'generate_lead', {
        event_category: 'Lead Generation',
        event_label: leadData.service,
        value: 1,
        custom_parameters: {
          source: leadData.source,
          service: leadData.service
        }
      });
    }

    // Facebook Pixel Tracking
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: leadData.service,
        content_category: 'Legal Services',
        value: 1,
        currency: 'USD'
      });
    }

    console.log('🎯 LEAD CAPTURED:', leadData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (!validateForm()) {
      e.preventDefault();
      return;
    }

    setIsSubmitting(true);
    
    // Track conversion before form submission
    trackConversion(formData);
    
    // Let Netlify handle the form submission natively
    // Don't prevent default - let it submit normally
    
    // Show success message after a brief delay
    setTimeout(() => {
      setIsSubmitted(true);
      toast.success('Messaggio inviato con successo! Ti contatteremo presto.');
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          service,
          source
        });
        setIsSubmitted(false);
        setIsSubmitting(false);
      }, 3000);
    }, 1000);
  };

  if (isSubmitted) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-8 text-center ${className}`}>
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-green-800 mb-2">
          Messaggio Inviato!
        </h3>
        <p className="text-green-700">
          Grazie per averci contattato. Ti risponderemo entro 24 ore.
        </p>
      </div>
    );
  }

  return (
    <form 
      onSubmit={handleSubmit} 
      className={`space-y-6 ${className}`}
      name="contact"
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
    >
      {/* Hidden field for Netlify */}
      <input type="hidden" name="form-name" value="contact" />
      
      {/* Honeypot field for spam protection */}
      <div style={{ display: 'none' }}>
        <label>
          Don't fill this out if you're human: <input name="bot-field" />
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-2">
            Nome Completo *
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Il tuo nome completo"
            required
            className="bg-white/10 border-white/20 text-white placeholder:text-blue-200"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email *
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="la-tua-email@esempio.com"
            required
            className="bg-white/10 border-white/20 text-white placeholder:text-blue-200"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="phone" className="block text-sm font-medium mb-2">
            Telefono (opzionale)
          </label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="+39 123 456 7890"
            className="bg-white/10 border-white/20 text-white placeholder:text-blue-200"
          />
        </div>
        <div>
          <label htmlFor="subject" className="block text-sm font-medium mb-2">
            Oggetto
          </label>
          <Input
            id="subject"
            name="subject"
            type="text"
            value={formData.subject}
            onChange={handleInputChange}
            placeholder="Oggetto della richiesta"
            className="bg-white/10 border-white/20 text-white placeholder:text-blue-200"
          />
        </div>
      </div>

      {/* Hidden fields for tracking */}
      <input type="hidden" name="service" value={formData.service} />
      <input type="hidden" name="source" value={formData.source} />

      <div>
        <label htmlFor="message" className="block text-sm font-medium mb-2">
          Messaggio *
        </label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          rows={5}
          placeholder="Descrivi la tua richiesta in dettaglio..."
          required
          className="bg-white/10 border-white/20 text-white placeholder:text-blue-200"
        />
      </div>

      <Button 
        type="submit" 
        size="lg" 
        disabled={isSubmitting}
        className="w-full bg-white text-blue-900 hover:bg-blue-50 disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-900 mr-2"></div>
            Invio in corso...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Invia Messaggio
          </>
        )}
      </Button>

      <p className="text-xs text-blue-200 text-center">
        * Campi obbligatori. I tuoi dati sono protetti e non verranno condivisi con terzi.
      </p>
    </form>
  );
};